/**
 * The practice firewall: practice cases never leave the browser.
 *
 * The training renders the real case components on invented cases whose ids start with
 * `PRACTICE-`. Those components call the API like on a real case. Every request goes
 * through the one generated client, whose `fetch` is {@link guardedFetch}:
 *   - a request that names a practice case (in the path, also URL-encoded, or in a JSON
 *     body as `case_id`, `case_ids` or `rows[].id`/`cases[].case_id`) is answered by the
 *     practice handler the training registered, as a local cache update;
 *   - with no handler registered it is answered 409 "Practice case: nothing is sent.";
 *   - in both cases the real `fetch` is never called for it;
 *   - every other request passes through unchanged.
 *
 * `navigator.sendBeacon` (the case activity pings) is wrapped the same way by
 * {@link installBeaconGuard}: a practice URL or body is dropped.
 *
 * Defence in depth: the server has no `PRACTICE-` case, so a request that got through
 * anyway would be a 404 that writes nothing.
 */

import { logger } from "@/lib/logger";

export const PRACTICE_PREFIX = "PRACTICE-";
export const PRACTICE_NOTHING_SENT = "Practice case: nothing is sent.";

/** Answers a practice request locally (the training's handler). */
export type PracticeHandler = (request: Request) => Promise<Response> | Response;

const ENCODED_RE = /PRACTICE(%2D|-)/i;

function textHasPractice(text: string | null | undefined): boolean {
  if (!text) {
    return false;
  }
  if (ENCODED_RE.test(text)) {
    return true;
  }
  try {
    return decodeURIComponent(text).includes(PRACTICE_PREFIX);
  } catch {
    return false;
  }
}

function idsInBody(value: unknown, depth = 0): boolean {
  if (depth > 4 || value == null) {
    return false;
  }
  if (typeof value === "string") {
    return value.startsWith(PRACTICE_PREFIX);
  }
  if (Array.isArray(value)) {
    return value.some((entry) => idsInBody(entry, depth + 1));
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    for (const key of ["case_id", "case_ids", "id", "rows", "cases", "ids"]) {
      if (key in record && idsInBody(record[key], depth + 1)) {
        return true;
      }
    }
  }
  return false;
}

/** Whether a request names a practice case (path, encoded path, or JSON body). */
export function isPracticeRequest(url: string, bodyText?: string | null): boolean {
  if (textHasPractice(url)) {
    return true;
  }
  if (!bodyText) {
    return false;
  }
  try {
    return idsInBody(JSON.parse(bodyText));
  } catch {
    // Not JSON: any mention of a practice id is enough to keep it local.
    return bodyText.includes(PRACTICE_PREFIX);
  }
}

let handler: PracticeHandler | null = null;
let handlerRefs = 0;

/**
 * Register the practice handler (or `null` to release one registration). Ref-counted:
 * two practice stages on one page share it, and the last one to unmount releases it.
 */
export function setPracticeHandler(next: PracticeHandler | null): void {
  if (next) {
    handler = next;
    handlerRefs += 1;
    return;
  }
  handlerRefs = Math.max(0, handlerRefs - 1);
  if (handlerRefs === 0) {
    handler = null;
  }
}

/** Whether a practice handler is registered (tests). */
export function hasPracticeHandler(): boolean {
  return handler !== null;
}

function nothingSent(): Response {
  return new Response(JSON.stringify({ detail: PRACTICE_NOTHING_SENT }), {
    status: 409,
    headers: { "Content-Type": "application/json" },
  });
}

async function bodyTextOf(input: RequestInfo | URL, init?: RequestInit): Promise<string | null> {
  const body = init?.body;
  if (typeof body === "string") {
    return body;
  }
  if (body instanceof URLSearchParams) {
    return body.toString();
  }
  if (input instanceof Request && !init?.body) {
    try {
      return await input.clone().text();
    } catch {
      return null;
    }
  }
  return null;
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.toString();
  }
  return input.url;
}

/**
 * `fetch` for the API client: practice requests go to the handler (or a 409), never to
 * the network; everything else is the real `fetch`, called once.
 */
export const guardedFetch: typeof fetch = async (input, init) => {
  const url = urlOf(input);
  const bodyText = await bodyTextOf(input, init);
  if (isPracticeRequest(url, bodyText)) {
    const active = handler;
    if (!active) {
      logger.warn("[training] practice request with no handler, nothing sent", url);
      return nothingSent();
    }
    const base = globalThis.location?.origin ?? "http://practice.local";
    const request =
      input instanceof Request && !init
        ? input
        : new Request(new URL(url, base), { ...init, body: bodyText });
    return active(request);
  }
  return globalThis.fetch(input, init);
};

let beaconInstalled = false;

/** Wrap `navigator.sendBeacon` so a practice URL or body is dropped (never sent). */
export function installBeaconGuard(): void {
  if (beaconInstalled || typeof navigator === "undefined") {
    return;
  }
  const original = navigator.sendBeacon?.bind(navigator);
  if (!original) {
    return;
  }
  const guarded = (url: string | URL, data?: BodyInit | null): boolean => {
    const body = typeof data === "string" ? data : null;
    if (isPracticeRequest(String(url), body)) {
      logger.debug("[training] practice beacon dropped", String(url));
      return true;
    }
    return original(url, data);
  };
  try {
    Object.defineProperty(navigator, "sendBeacon", { value: guarded, configurable: true });
  } catch {
    (navigator as { sendBeacon: typeof guarded }).sendBeacon = guarded;
  }
  beaconInstalled = true;
}

/** Undo {@link installBeaconGuard} bookkeeping (tests only). */
export function resetBeaconGuardForTests(): void {
  beaconInstalled = false;
}
